/**
 * What opens when a pin is clicked, when a pin can stand for more than one
 * company.
 *
 * Companies are placed by address, so an office building is one coordinate with
 * a dozen companies on it. The map draws that as a single pin (see
 * `groupByPosition`), which means the click has to answer "which of them" before
 * it can answer "tell me about it".
 *
 * One pin, one company: straight to the company card, exactly as before.
 * One pin, many: the list first, then the company card with a way back. The
 * list is not a menu that disappears - it is where the operator returns to, so
 * the back arrow is on the card rather than the address being re-clicked.
 */
import { useEffect, useState } from "react";
import { Building2, ChevronRight, X } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { MapPoint } from "../../../../lib/companyMap";
import { fmtMoneyShort } from "../../../ui";
import { WebNeedPill } from "../webneed";
import { MapCompanyCard } from "./MapCompanyCard";

/** The chooser: every company registered at this one address. */
function AddressList({
  group,
  onPick,
  onClose,
}: {
  group: MapPoint[];
  onPick: (point: MapPoint) => void;
  onClose: () => void;
}) {
  const t = useT();
  const address = group.find((p) => p.addressFull)?.addressFull ?? null;

  return (
    <div className="crm-card crm-mapcard absolute bottom-4 z-10 flex max-h-[min(28rem,calc(100%-2rem))] w-[min(22rem,calc(100%-2rem))] flex-col p-4 shadow-lg">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-[650]">
            {t("ariregister.map.atAddress", { n: group.length })}
          </p>
          {address ? (
            <p className="crm-mut mt-1 flex items-start gap-1.5 text-[12.5px] leading-snug">
              <Building2 className="mt-0.5 size-3.5 shrink-0 opacity-60" />
              <span className="min-w-0">{address}</span>
            </p>
          ) : null}
        </div>
        <button type="button" onClick={onClose} className="crm-cb crm-cb-sm shrink-0" aria-label={t("common.close")}>
          <X className="size-3.5" />
        </button>
      </div>

      {/* Its own scroll box: a registered-office address in Tallinn can carry
          forty companies, and a card that grew to fit them would run off the
          bottom of the map. */}
      <ul className="mt-3 min-h-0 flex-1 space-y-1 overflow-y-auto border-t border-[var(--crm-hair)] pt-2">
        {group.map((p) => (
          <li key={p.regCode}>
            <button
              type="button"
              onClick={() => onPick(p)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-[var(--crm-sunk)]"
            >
              <span className="min-w-0 flex-1">
                <span className="crm-trunc block text-[13px] font-[600]">{p.name}</span>
                <span className="crm-mut crm-tnum block text-[11.5px]">
                  {p.regCode}
                  {p.revenueCents ? ` · ${fmtMoneyShort(Number(p.revenueCents))}` : ""}
                </span>
              </span>
              <WebNeedPill score={p.webNeedScore} />
              <ChevronRight className="crm-mut size-3.5 shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MapPickCard({ group, onClose }: { group: MapPoint[]; onClose: () => void }) {
  const [chosen, setChosen] = useState<MapPoint | null>(null);

  // A different pin was clicked while this card was up. Without this the card
  // would keep showing whichever company was drilled into at the last address.
  useEffect(() => {
    setChosen(null);
  }, [group]);

  if (group.length === 0) return null;

  const single = group.length === 1 ? (group[0] as MapPoint) : null;
  const point = single ?? chosen;

  if (!point) return <AddressList group={group} onPick={setChosen} onClose={onClose} />;

  return (
    <MapCompanyCard
      point={point}
      onClose={onClose}
      // Only when there is a list to go back TO. A single-company pin has none,
      // and an arrow that returned to nothing would be a dead control.
      onBack={single ? undefined : () => setChosen(null)}
    />
  );
}
